import { useState } from 'react';
import type { RoomCompletionResult } from '@crackpoint/shared/types';
import { strings } from '../../config';
import { useTheme } from '../ui/ThemeProvider';
import { Button } from '../ui/Button';

interface RoomCompleteScreenProps {
  result: RoomCompletionResult;
  solution: number[];
  attemptCount: number;
  onAdvance: () => void;
}

export function RoomCompleteScreen({ result, solution, attemptCount, onAdvance }: RoomCompleteScreenProps) {
  const { theme } = useTheme();
  const [showDevSolution, setShowDevSolution] = useState(false);

  const stars = Array.from({ length: 3 }, (_, i) => i < result.stars_earned);
  const hasLoot = result.loot !== null;
  const hasBonusLives = result.bonus_lives > 0;

  const getPowerUpColour = (type: 'oracle' | 'surge' | 'boost'): string => {
    if (type === 'surge') {
      return theme.colours.powerUp.surge_i;
    }
    return theme.colours.powerUp[type];
  };

  return (
    <div
      className="room-complete-screen"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: theme.colours.background.primary,
        padding: theme.spacing.xl,
        fontFamily: theme.typography.fontFamily,
        overflow: 'auto',
      }}
    >
      <div
        className="room-complete-screen__content"
        style={{
          maxWidth: '600px',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: theme.spacing.xl,
          alignItems: 'center',
        }}
      >
        <h1
          style={{
            fontSize: theme.typography.fontSize.xxl,
            fontWeight: theme.typography.fontWeight.bold,
            color: theme.colours.accent.success,
            margin: 0,
          }}
        >
          {strings.roomComplete.title}
        </h1>

        <div
          className="room-complete-screen__stars"
          style={{
            display: 'flex',
            gap: theme.spacing.md,
            fontSize: '4rem',
          }}
        >
          {stars.map((earned, i) => (
            <span key={i} style={{ opacity: earned ? 1 : 0.3 }}>
              {earned ? '⭐' : '☆'}
            </span>
          ))}
        </div>

        <div
          className="room-complete-screen__details"
          style={{
            width: '100%',
            backgroundColor: theme.colours.background.elevated,
            borderRadius: theme.borders.radius.lg,
            padding: theme.spacing.lg,
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing.md,
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              fontSize: theme.typography.fontSize.md,
              color: theme.colours.text.primary,
            }}
          >
            <span>{strings.roomComplete.codeLabel}</span>
            <span
              style={{
                fontFamily: '"JetBrains Mono", monospace',
                fontWeight: theme.typography.fontWeight.bold,
                color: theme.colours.accent.primary,
                letterSpacing: '0.2em',
              }}
            >
              {solution.join(' ')}
            </span>
          </div>

          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              fontSize: theme.typography.fontSize.md,
              color: theme.colours.text.primary,
            }}
          >
            <span>{strings.roomComplete.optimalHintsLabel}</span>
            <span style={{ fontWeight: theme.typography.fontWeight.semibold }}>{result.optimal_hint_count}</span>
          </div>

          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              fontSize: theme.typography.fontSize.md,
              color: theme.colours.text.primary,
            }}
          >
            <span>{strings.roomComplete.yourAttemptsLabel}</span>
            <span style={{ fontWeight: theme.typography.fontWeight.semibold }}>{attemptCount}</span>
          </div>
        </div>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing.sm,
            fontSize: theme.typography.fontSize.sm,
            color: theme.colours.text.secondary,
          }}
        >
          <input
            type="checkbox"
            id="show-dev-solution"
            checked={showDevSolution}
            onChange={(e) => setShowDevSolution(e.target.checked)}
            style={{ cursor: 'pointer' }}
          />
          <label htmlFor="show-dev-solution" style={{ cursor: 'pointer' }}>
            {strings.roomComplete.devSolutionLabel}
          </label>
        </div>

        {showDevSolution && (
          <div
            style={{
              width: '100%',
              backgroundColor: theme.colours.background.secondary,
              borderRadius: theme.borders.radius.md,
              padding: theme.spacing.md,
              fontSize: theme.typography.fontSize.sm,
              color: theme.colours.text.tertiary,
              fontFamily: '"JetBrains Mono", monospace',
            }}
          >
            {strings.roomComplete.devSolutionPlaceholder}
          </div>
        )}

        {hasLoot && result.loot && (
          <div
            style={{
              width: '100%',
              backgroundColor: getPowerUpColour(result.loot.type),
              borderRadius: theme.borders.radius.lg,
              padding: theme.spacing.lg,
              display: 'flex',
              alignItems: 'center',
              gap: theme.spacing.md,
              color: theme.colours.text.inverse,
            }}
          >
            <span style={{ fontSize: '2rem' }}>{strings.roomComplete.lootIcon}</span>
            <div>
              <div style={{ fontSize: theme.typography.fontSize.lg, fontWeight: theme.typography.fontWeight.bold }}>
                {strings.roomComplete.lootTitle}
              </div>
              <div style={{ fontSize: theme.typography.fontSize.sm }}>
                {strings.roomComplete.powerUpMessage(result.loot.type)}
              </div>
            </div>
          </div>
        )}

        {hasBonusLives && (
          <div
            style={{
              width: '100%',
              backgroundColor: theme.colours.accent.success,
              borderRadius: theme.borders.radius.lg,
              padding: theme.spacing.lg,
              display: 'flex',
              alignItems: 'center',
              gap: theme.spacing.md,
              color: theme.colours.text.inverse,
            }}
          >
            <span style={{ fontSize: '2rem' }}>{strings.roomComplete.bonusLivesIcon}</span>
            <div>
              <div style={{ fontSize: theme.typography.fontSize.lg, fontWeight: theme.typography.fontWeight.bold }}>
                {strings.roomComplete.bonusLivesTitle}
              </div>
              <div style={{ fontSize: theme.typography.fontSize.sm }}>
                {strings.roomComplete.bonusLivesMessage(result.bonus_lives)}
              </div>
            </div>
          </div>
        )}

        <Button variant="primary" size="lg" fullWidth onClick={onAdvance}>
          {strings.roomComplete.nextRoomButton}
        </Button>

        <div
          style={{
            fontSize: theme.typography.fontSize.sm,
            color: theme.colours.text.tertiary,
          }}
        >
          {strings.roomComplete.enterHint}
        </div>
      </div>
    </div>
  );
}
